import React, { useState, useEffect, useCallback } from 'react';
import { invInwardService, invItemService, invSupplierService, invPOService, invUnitService } from '../../services/inventoryService';
import { card, pageWrap, btn, inputStyle, selectStyle, label, fieldGroup, modal, modalBox,
  sectionTitle, errorBox, emptyState, fmtCost, fmtDate, fmtQty, friendlyErr } from './inventoryStyles';

const defaultLine = { inventory_item_id: '', received_qty: '', purchase_unit_id: '', conversion_factor: '1', base_qty: '', purchase_price: '', line_value: '0' };

const buildInitialState = (po) => {
  if (!po) return { form: { supplier_id: '', po_id: '', inward_date: new Date().toISOString().slice(0, 10), invoice_ref: '', notes: '' }, lineItems: [defaultLine] };
  const form = {
    supplier_id: po.supplier_id || po.supplier?.id || '',
    po_id: po.id,
    inward_date: new Date().toISOString().slice(0, 10),
    invoice_ref: '',
    notes: '',
  };
  const lineItems = (po.items || []).map(line => {
    const itemId = line.inventory_item_id || line.inventory_item?.id || '';
    const unitId = line.purchase_unit_id || line.purchase_unit?.id || '';
    const rcvQty = parseFloat(line.ordered_qty) || 0;
    const price  = parseFloat(line.unit_price) || 0;
    return {
      inventory_item_id: itemId,
      received_qty:      String(line.ordered_qty || ''),
      purchase_unit_id:  unitId,
      conversion_factor: '1',
      base_qty:          rcvQty > 0 ? rcvQty.toFixed(4) : '',
      purchase_price:    String(price || ''),
      line_value:        (rcvQty * price).toFixed(2),
    };
  });
  return { form, lineItems: lineItems.length > 0 ? lineItems : [defaultLine] };
};

const StockInward = ({ restaurant, initialPO, onSaved }) => {
  const init = buildInitialState(initialPO);
  const [inwards, setInwards]     = useState([]);
  const [items, setItems]         = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [units, setUnits]         = useState([]);
  const [openPOs, setOpenPOs]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [viewGRN, setViewGRN]     = useState(null);
  const [form, setForm]           = useState(init.form);
  const [lineItems, setLineItems] = useState(init.lineItems);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const restaurantId = restaurant?.id;

  const load = useCallback(async () => {
    setLoading(true);
    const [iRes, itemsRes, sRes, poRes, uRes] = await Promise.all([
      invInwardService.getAll(restaurantId),
      invItemService.getAll(restaurantId),
      invSupplierService.getAll(restaurantId),
      invPOService.getAll(restaurantId),
      invUnitService.getAll(restaurantId),
    ]);
    setInwards(iRes.data || []);
    setItems((itemsRes.data || []).filter(i => i.is_active));
    setSuppliers((sRes.data || []).filter(s => s.is_active));
    setOpenPOs((poRes.data || []).filter(p => ['draft','sent','part_received'].includes(p.status)));
    setUnits(uRes.data || []);
    setLoading(false);
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  // Open form exactly once on mount if navigated from a PO — empty deps guarantees no re-trigger
  useEffect(() => { if (initialPO) setShowForm(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setForm({ supplier_id: '', po_id: '', inward_date: new Date().toISOString().slice(0, 10), invoice_ref: '', notes: '' });
    setLineItems([{ inventory_item_id: '', received_qty: '', purchase_unit_id: '', conversion_factor: '1', base_qty: '', purchase_price: '', line_value: '0' }]);
    setError(''); setShowForm(true);
  };

  const addLine = () => setLineItems(p => [...p, { inventory_item_id: '', received_qty: '', purchase_unit_id: '', conversion_factor: '1', base_qty: '', purchase_price: '', line_value: '0' }]);
  const removeLine = (i) => setLineItems(p => p.filter((_, idx) => idx !== i));

  const updateLine = (idx, field, val) => {
    setLineItems(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      const updated = { ...l, [field]: val };
      // When item changes, auto-fill conversion factor from item master
      if (field === 'inventory_item_id') {
        const item = items.find(it => it.id === val);
        if (item) {
          updated.purchase_unit_id = item.purchase_unit_id || '';
          updated.conversion_factor = String(item.conversion_factor || 1);
          updated.purchase_price = item.latest_purchase_price ? String(item.latest_purchase_price) : updated.purchase_price;
        }
      }
      // Recalculate base_qty and line_value
      const rcvQty  = parseFloat(field === 'received_qty'       ? val : updated.received_qty)       || 0;
      const convFac = parseFloat(field === 'conversion_factor'  ? val : updated.conversion_factor)  || 1;
      const price   = parseFloat(field === 'purchase_price'     ? val : updated.purchase_price)     || 0;
      const baseQty = rcvQty * convFac;
      updated.base_qty   = baseQty > 0 ? baseQty.toFixed(4) : '';
      updated.line_value = (baseQty * price).toFixed(2);
      return updated;
    }));
  };

  const totalValue = lineItems.reduce((s, l) => s + (parseFloat(l.line_value) || 0), 0);

  const save = async () => {
    if (lineItems.length === 0 || lineItems.some(l => !l.inventory_item_id || !l.received_qty || parseFloat(l.received_qty) <= 0)) {
      setError('Add at least one line item with item and quantity > 0.'); return;
    }
    setSaving(true); setError('');
    const preparedLines = lineItems.map(l => ({
      ...l,
      base_qty: parseFloat(l.base_qty) || parseFloat(l.received_qty) || 0,
    }));
    try {
      const { error: e } = await invInwardService.create(restaurantId, form, preparedLines);
      setSaving(false);
      if (e) { setError(friendlyErr(e)); return; }
      setShowForm(false);
      onSaved ? onSaved() : load();
    } catch (err) {
      setSaving(false);
      setError(friendlyErr(err));
    }
  };

  return (
    <div style={pageWrap}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: '#111' }}>Stock Inward / GRN</h2>
            <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.88rem' }}>Goods Received Notes stock, updates on save</p>
          </div>
          <button style={btn('primary')} onClick={openNew}>+ Receive Stock</button>
        </div>

        <div style={{ ...card, overflow: 'hidden' }}>
          {loading ? (
            <div style={emptyState}>Loading…</div>
          ) : inwards.length === 0 ? (
            <div style={emptyState}>
              <div style={{ fontWeight: '600', color: '#555', marginBottom: '8px' }}>No inward entries yet</div>
              <div>Record stock received from suppliers.</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #f3f4f6' }}>
                  {['GRN Number', 'Date', 'Source', 'Supplier', 'Items', 'Total Value', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.8rem' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inwards.map(grn => (
                  <tr key={grn.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 14px', fontWeight: '700', color: '#333', fontFamily: 'monospace' }}>{grn.grn_number}</td>
                    <td style={{ padding: '12px 14px', color: '#555' }}>{fmtDate(grn.inward_date)}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ padding: '3px 10px', background: grn.source_type === 'po' ? '#eff6ff' : '#f9fafb', color: grn.source_type === 'po' ? '#2563eb' : '#666', borderRadius: '10px', fontSize: '0.78rem', fontWeight: '600', border: `1px solid ${grn.source_type === 'po' ? '#bfdbfe' : '#e2e8f0'}` }}>
                        {grn.source_type === 'po' ? `PO: ${grn.po?.po_number || ''}` : 'Manual'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', color: '#555' }}>{grn.supplier?.name || '—'}</td>
                    <td style={{ padding: '12px 14px', color: '#555' }}>{grn.items?.length || 0}</td>
                    <td style={{ padding: '12px 14px', fontWeight: '700', color: '#16a34a' }}>{fmtCost(grn.total_value)}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <button style={{ ...btn('outline'), padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => setViewGRN(grn)}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Receive Stock Modal */}
      {showForm && (
        <div style={modal} onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div style={{ ...modalBox, maxWidth: '820px' }}>
            <h3 style={{ ...sectionTitle, marginBottom: '20px' }}>Receive Stock (GRN)</h3>
            {error && <div style={errorBox}>{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
              <div style={fieldGroup}>
                <label style={label}>Against PO</label>
                <select style={selectStyle()} value={form.po_id} onChange={e => setForm(f => ({ ...f, po_id: e.target.value }))}>
                  <option value="">Manual (no PO)</option>
                  {openPOs.map(po => <option key={po.id} value={po.id}>{po.po_number}</option>)}
                </select>
              </div>
              <div style={fieldGroup}>
                <label style={label}>Supplier</label>
                <select style={selectStyle()} value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))}>
                  <option value="">Select (optional)</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div style={fieldGroup}>
                <label style={label}>Inward Date</label>
                <input style={inputStyle()} type="date" value={form.inward_date} onChange={e => setForm(f => ({ ...f, inward_date: e.target.value }))} />
              </div>
              <div style={fieldGroup}>
                <label style={label}>Invoice Reference</label>
                <input style={inputStyle()} value={form.invoice_ref} onChange={e => setForm(f => ({ ...f, invoice_ref: e.target.value }))} placeholder="Supplier invoice no." />
              </div>
              <div style={{ ...fieldGroup, gridColumn: '2 / -1' }}>
                <label style={label}>Notes</label>
                <input style={inputStyle()} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
              </div>
            </div>

            {/* Line Items */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label style={{ ...label, margin: 0 }}>Items Received *</label>
                <button style={{ ...btn('outline'), padding: '5px 12px', fontSize: '0.8rem' }} onClick={addLine}>+ Add Item</button>
              </div>
              {/* Column headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr auto', gap: '6px', marginBottom: '6px', padding: '0 2px' }}>
                {['Item', 'Rcv Qty', 'Unit', 'Conv. Factor', 'Base Qty', 'Price/base unit', ''].map((h, i) => (
                  <div key={i} style={{ fontSize: '0.75rem', fontWeight: '600', color: '#888' }}>{h}</div>
                ))}
              </div>
              {lineItems.map((line, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr auto', gap: '6px', marginBottom: '8px', alignItems: 'end' }}>
                  <select style={selectStyle(!line.inventory_item_id)} value={line.inventory_item_id} onChange={e => updateLine(idx, 'inventory_item_id', e.target.value)}>
                    <option value="">Select item…</option>
                    {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                  <input style={inputStyle(!line.received_qty)} type="number" min="0.001" step="0.001" value={line.received_qty} onChange={e => updateLine(idx, 'received_qty', e.target.value)} placeholder="0" />
                  <select style={selectStyle()} value={line.purchase_unit_id} onChange={e => updateLine(idx, 'purchase_unit_id', e.target.value)}>
                    <option value="">Base</option>
                    {units.map(u => <option key={u.id} value={u.id}>{u.short_code}</option>)}
                  </select>
                  <input style={inputStyle()} type="number" min="0.0001" step="0.0001" value={line.conversion_factor} onChange={e => updateLine(idx, 'conversion_factor', e.target.value)} title="Base units per purchase unit" />
                  <input style={{ ...inputStyle(), background: '#f9fafb', color: '#555' }} readOnly value={line.base_qty || ''} placeholder="auto" />
                  <input style={inputStyle()} type="number" min="0" step="0.01" value={line.purchase_price} onChange={e => updateLine(idx, 'purchase_price', e.target.value)} placeholder="₹0" />
                  <button style={{ ...btn('danger'), padding: '8px 10px', fontSize: '0.82rem' }} onClick={() => removeLine(idx)}>✕</button>
                </div>
              ))}
              <div style={{ textAlign: 'right', padding: '12px', background: '#f9fafb', borderRadius: '10px', fontWeight: '800', fontSize: '1rem', color: '#111' }}>
                Total Value: {fmtCost(totalValue)}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button style={btn('outline')} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={btn('primary', saving)} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save GRN & Update Stock'}</button>
            </div>
          </div>
        </div>
      )}

      {/* View GRN Modal */}
      {viewGRN && (
        <div style={modal} onClick={e => { if (e.target === e.currentTarget) setViewGRN(null); }}>
          <div style={{ ...modalBox, maxWidth: '620px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ ...sectionTitle, margin: 0 }}>{viewGRN.grn_number}</h3>
              <button style={{ ...btn('outline'), padding: '6px 14px', fontSize: '0.85rem' }} onClick={() => setViewGRN(null)}>Close</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '0.88rem', marginBottom: '20px' }}>
              <div><span style={{ color: '#888' }}>Date:</span> <strong>{fmtDate(viewGRN.inward_date)}</strong></div>
              <div><span style={{ color: '#888' }}>Supplier:</span> <strong>{viewGRN.supplier?.name || '—'}</strong></div>
              <div><span style={{ color: '#888' }}>Invoice Ref:</span> <strong>{viewGRN.invoice_ref || '—'}</strong></div>
              <div><span style={{ color: '#888' }}>Total:</span> <strong style={{ color: '#16a34a' }}>{fmtCost(viewGRN.total_value)}</strong></div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Item', 'Base Qty', 'Price', 'Value'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.78rem' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(viewGRN.items || []).map((l, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px', fontWeight: '600' }}>{l.inventory_item?.name}</td>
                    <td style={{ padding: '10px 12px' }}>{fmtQty(l.base_qty)} {l.purchase_unit?.short_code || ''}</td>
                    <td style={{ padding: '10px 12px' }}>{fmtCost(l.purchase_price)}</td>
                    <td style={{ padding: '10px 12px', fontWeight: '600' }}>{fmtCost(l.line_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockInward;
